/**
 * Consent Popup Component
 * Handles cookie and data processing consent for both logged and anonymous users
 * Standalone component with Angular Signals
 */

import {
  Component,
  signal,
  computed,
  inject,
  OnInit,
  OnDestroy,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../core/services/auth.service';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import { ConsentPopupService } from '../../../core/services/consent-popup.service';
import { SplashScreenService } from '../../../core/services/splash-screen.service';
import { PlatformService } from '../../../core/services/platform.service';
import {
  ConsentRecord,
  ConsentRecordHelper,
  CookiePreferences,
} from '../../../core/models';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { ExternalBrowserService } from '../../../core/services/external-browser.service';

@Component({
  selector: 'app-consent-popup',
  standalone: true,
  imports: [CommonModule, LoadingSpinnerComponent, TranslateModule],
  templateUrl: './consent-popup.component.html',
  styleUrls: ['./consent-popup.component.scss'],
})
export class ConsentPopupComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private storageService = inject(LocalStorageService);
  private consentPopupService = inject(ConsentPopupService);
  private splashScreenService = inject(SplashScreenService);
  private externalBrowser = inject(ExternalBrowserService);
  private platformService = inject(PlatformService);

  // Signals
  isVisible = signal(false);
  showCookieConfig = signal(false);
  showLegalInfo = signal(false);

  // Loading state - shows spinner while waiting for API response
  isProcessing = signal(false);
  processingError = signal<string | null>(null);

  // Flag to prevent multiple simultaneous consent saves
  private isSaving = signal(false);

  // Loading spinner messages
  readonly loadingPhrases = [
    'Configurando tu experiencia...',
    'Preparando las mejores ofertas para ti...',
    'Personalizando tu viaje gastronómico...',
    'Casi listo, un momento...',
  ];

  // Cookie types configuration
  cookiePreferences = signal<CookiePreferences>({
    necessary: true,
    functional: true,
    analytics: true,
    marketing: true,
  });

  // Computed
  isAuthenticated = computed(() => this.authService.isAuthenticated());
  isTemporal = computed(() => this.authService.isTemporal());

  // Store original body styles for scroll locking
  private originalBodyStyles: any = null;
  private scrollPosition = 0;

  // Banner configuration
  private readonly BANNER_VERSION = '1.0.0';
  private readonly CONSENT_DURATION_DAYS = 365;

  constructor() {
    // Watch for authentication state changes
    // When user logs out or session is cleared (401), get new JWT silently
    effect(() => {
      const userContext = this.authService.userContext();
      const authToken = this.storageService.getAuthToken();

      // If userContext is null and no authToken, user has been logged out or cleared
      // Get new JWT silently (no popup shown)
      if (!userContext && !authToken && !this.isSaving()) {
        // Small delay to allow other components to finish cleaning up
        setTimeout(() => {
          this.checkConsentNeeded();
        }, 100);
      }
    });

    // Watch for consent requests from other services (e.g., location service)
    // Instead of showing popup, just auto-accept silently
    effect(() => {
      const shouldShow = this.consentPopupService.shouldShow();
      const splashComplete = this.splashScreenService.isComplete();

      // If consent is requested and splash is complete, auto-accept silently
      if (shouldShow && splashComplete && !this.isSaving()) {
        console.log('🔐 Consent requested by service - auto-accepting silently');
        this.checkConsentNeeded();
      }
    });

    // Watch for splash screen completion to trigger initial JWT check
    effect(() => {
      const splashComplete = this.splashScreenService.isComplete();
      if (splashComplete) {
        console.log('🔐 Splash complete - checking JWT');
        this.checkConsentNeeded();
      }
    });
  }

  ngOnInit(): void {
    // Don't check consent immediately - wait for splash screen to complete
    // The effect above will trigger checkConsentNeeded when splash is done
  }

  ngOnDestroy(): void {
    this.enableBodyScroll();
  }

  /**
   * Check if consent/JWT is needed
   *
   * Platform-specific behavior:
   * - Web: Shows the consent popup banner for user interaction
   * - iOS/Android: Silently auto-accepts to avoid App Store rejection (Guideline 5.1.2)
   *
   * Both approaches create a temporal user session with JWT for API access.
   */
  async checkConsentNeeded(): Promise<void> {
    // Don't check if we're currently saving consent
    if (this.isSaving()) {
      console.log('🍪 Skipping consent check - save in progress');
      return;
    }

    try {
      const hasConsent = await this.hasUserGivenConsent();

      // Update consent status in the service (for components waiting on shared links)
      this.consentPopupService.setConsentStatus(hasConsent);

      if (!hasConsent) {
        // Check platform - show popup only on web, auto-accept on iOS/Android
        if (this.platformService.isWeb()) {
          // Web: Show the consent popup banner
          console.log('🔐 No JWT found (Web) - showing consent popup');
          this.show();
        } else {
          // iOS/Android: Auto-accept silently to avoid App Store rejection
          console.log('🔐 No JWT found (Native) - auto-accepting to get session token (no popup)');
          await this.autoAcceptSilently();
        }
      }
    } catch (error) {
      console.error('Error checking consent status:', error);
      // Auto-accept on error too to ensure app functions
      console.log('🔐 Error checking consent - auto-accepting to get session token');
      await this.autoAcceptSilently();
    }
  }

  /**
   * Auto-accept essential preferences silently (no popup shown)
   * Creates temporal user with essential-only preferences to get JWT
   */
  private async autoAcceptSilently(): Promise<void> {
    this.isSaving.set(true);
    this.consentPopupService.startSaving();

    try {
      const consentRecord = ConsentRecordHelper.createConsentRecord(
        true, // cookieConsent
        true, // dataProcessingConsent
        ConsentRecordHelper.getEssentialOnlyCookiePreferences(),
        {
          userId: this.authService.currentUser()?.id,
          userEmail: this.authService.currentUser()?.email,
          sessionId: this.authService.getSessionId() || undefined,
          consentMethod: 'essential_only',
        },
      );

      await this.saveConsent(consentRecord);
      this.consentPopupService.setConsentStatus(true);
      console.log('✅ Session token obtained successfully (silent accept)');
    } catch (error) {
      console.error('❌ Error getting session token:', error);
      // Still mark as consented to allow app to function
      this.consentPopupService.setConsentStatus(true);
    } finally {
      this.isSaving.set(false);
      this.consentPopupService.finishSaving();
    }
  }

  /**
   * Check if user has already given consent
   * If authToken exists in unified storage, user has accepted policies and is authenticated/temporal
   */
  private async hasUserGivenConsent(): Promise<boolean> {
    // If we're currently saving, consider consent as given to prevent duplicate popups
    if (this.isSaving()) {
      return true;
    }

    // Check if authToken exists in unified storage
    // If token exists, user has accepted policies (backend validates)
    const authToken = this.storageService.getAuthToken();

    if (
      authToken &&
      typeof authToken === 'string' &&
      authToken.trim().length > 0
    ) {
      console.log(
        '✅ Valid consent found: authToken present in unified storage',
      );
      return true;
    }

    console.log('❌ No valid authToken found in unified storage');
    return false;
  }

  /**
   * Show the consent popup
   */
  show(): void {
    // Don't show if we're currently saving
    if (this.isSaving()) {
      console.log('🍪 Not showing popup - save in progress');
      return;
    }
    this.originalBodyStyles = {
      height: document.body.style.height,
      overflowY: document.body.style.overflowY,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };

    this.isVisible.set(true);
    this.disableBodyScroll();
  }

  /**
   * Hide the consent popup
   */
  hide(): void {
    this.isVisible.set(false);
    this.isProcessing.set(false);
    this.processingError.set(null);
    this.enableBodyScroll();
    // Reset the service state
    this.consentPopupService.hideConsent();
  }

  /**
   * Handle save error - show error message and re-show consent popup
   */
  private handleSaveError(error: unknown): void {
    console.error('❌ Error saving consent:', error);
    this.isProcessing.set(false);

    // Set error message
    const errorMessage = error instanceof Error
      ? error.message
      : 'Error de conexión. Por favor, inténtalo de nuevo.';
    this.processingError.set(errorMessage);

    // The popup stays visible with error message, user needs to try again
  }

  /**
   * Retry consent after error
   */
  retryConsent(): void {
    this.processingError.set(null);
  }

  /**
   * Disable body scroll
   */
  private disableBodyScroll(): void {
    this.scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;

    document.body.style.height = '100%';
    document.body.style.overflowY = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';

    document.body.classList.add('consent-popup-open');
    document.documentElement.classList.add('consent-popup-open');
  }

  /**
   * Enable body scroll
   */
  private enableBodyScroll(): void {
    document.body.classList.remove('consent-popup-open');
    document.documentElement.classList.remove('consent-popup-open');

    // Clear all popup-related styles
    document.body.style.height = '';
    document.body.style.overflowY = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    // Restore scroll position
    requestAnimationFrame(() => {
      window.scrollTo(0, this.scrollPosition);
    });
  }

  /**
   * Handle accept all cookies
   */
  async handleAccept(): Promise<void> {
    // Prevent multiple simultaneous saves
    if (this.isSaving()) {
      console.log('🍪 Already saving consent, ignoring duplicate click');
      return;
    }

    this.isSaving.set(true);
    this.isProcessing.set(true);
    this.processingError.set(null);
    this.consentPopupService.startSaving(); // Notify service (prevents 401 from clearing data)

    try {
      const consentRecord = ConsentRecordHelper.createConsentRecord(
        true,
        true,
        ConsentRecordHelper.getDefaultCookiePreferences(),
        {
          userId: this.authService.currentUser()?.id,
          userEmail: this.authService.currentUser()?.email,
          sessionId: this.authService.getSessionId() || undefined,
          consentMethod: 'banner_interaction',
        },
      );

      await this.saveConsent(consentRecord);
      this.hide();
      console.log('Consent accepted successfully', consentRecord);
    } catch (error) {
      console.error('Error saving consent:', error);
      this.handleSaveError(error);
    } finally {
      this.isSaving.set(false);
      this.consentPopupService.finishSaving();
    }
  }

  /**
   * Handle essential only
   */
  async handleEssentialOnly(): Promise<void> {
    // Prevent multiple simultaneous saves
    if (this.isSaving()) {
      console.log('🍪 Already saving consent, ignoring duplicate click');
      return;
    }

    this.isSaving.set(true);
    this.isProcessing.set(true);
    this.processingError.set(null);
    this.consentPopupService.startSaving(); // Notify service (prevents 401 from clearing data)

    try {
      const consentRecord = ConsentRecordHelper.createConsentRecord(
        false,
        false,
        ConsentRecordHelper.getEssentialOnlyCookiePreferences(),
        {
          userId: this.authService.currentUser()?.id,
          userEmail: this.authService.currentUser()?.email,
          sessionId: this.authService.getSessionId() || undefined,
          consentMethod: 'essential_only',
        },
      );

      await this.saveConsent(consentRecord);
      this.hide();
      console.log('Essential cookies only accepted', consentRecord);
    } catch (error) {
      console.error('Error saving essential cookies consent:', error);
      this.handleSaveError(error);
    } finally {
      this.isSaving.set(false);
      this.consentPopupService.finishSaving();
    }
  }

  /**
   * Show cookie configuration modal
   */
  handleConfigure(): void {
    this.showCookieConfig.set(true);
  }

  /**
   * Close cookie configuration modal
   */
  closeCookieConfig(): void {
    this.showCookieConfig.set(false);
  }

  /**
   * Save cookie configuration
   */
  async saveCookieConfig(): Promise<void> {
    // Prevent multiple simultaneous saves
    if (this.isSaving()) {
      console.log('🍪 Already saving consent, ignoring duplicate click');
      return;
    }

    this.isSaving.set(true);
    this.isProcessing.set(true);
    this.processingError.set(null);
    this.closeCookieConfig();
    this.consentPopupService.startSaving(); // Notify service (prevents 401 from clearing data)

    try {
      const prefs = this.cookiePreferences();
      const hasAnyOptional =
        prefs.functional || prefs.analytics || prefs.marketing;

      const consentRecord = ConsentRecordHelper.createConsentRecord(
        hasAnyOptional,
        hasAnyOptional,
        prefs,
        {
          userId: this.authService.currentUser()?.id,
          userEmail: this.authService.currentUser()?.email,
          sessionId: this.authService.getSessionId() || undefined,
          consentMethod: 'cookie_configuration',
        },
      );

      await this.saveConsent(consentRecord);
      this.hide();
      console.log('Custom cookie configuration saved', consentRecord);
    } catch (error) {
      console.error('Error saving cookie configuration:', error);
      this.handleSaveError(error);
    } finally {
      this.isSaving.set(false);
      this.consentPopupService.finishSaving();
    }
  }

  /**
   * Show more info modal
   */
  handleMoreInfo(): void {
    this.showLegalInfo.set(true);
  }

  /**
   * Close legal info modal
   */
  closeLegalInfo(): void {
    this.showLegalInfo.set(false);
  }

  /**
   * Save consent (authenticated or temporal user)
   */
  private async saveConsent(consentRecord: ConsentRecord): Promise<void> {
    const isAuth = this.authService.isAuthenticated();

    if (isAuth) {
      // For authenticated users, save to server
      await this.saveConsentForAuthenticatedUser(consentRecord);
    } else {
      // For anonymous/temporal users, create temporal user or save locally
      await this.saveConsentForTemporalUser(consentRecord);
    }
  }

  /**
   * Save consent for authenticated user
   */
  private async saveConsentForAuthenticatedUser(
    consentRecord: ConsentRecord,
  ): Promise<void> {
    try {
      console.log('🔄 Updating privacy preferences for authenticated user...');

      const privacyPreferences = {
        cookieConsent: consentRecord.cookieConsent,
        dataProcessingConsent: consentRecord.dataProcessingConsent,
        cookiePreferences: consentRecord.cookiePreferences,
        bannerVersion: consentRecord.bannerVersion,
        consentMethod: consentRecord.consentMethod,
        consentDurationDays: consentRecord.consentDurationDays,
        bannerText: consentRecord.bannerText,
      };

      // Update preferences on the backend
      await this.authService.updatePrivacyPolicies(privacyPreferences);
      console.log('✅ Privacy preferences updated successfully on backend');

      // Also save to localStorage
      this.storageService.storeUserConsent(consentRecord);
    } catch (error) {
      console.error('❌ Error updating privacy preferences on server:', error);
      // Fallback to localStorage
      this.storageService.storeUserConsent(consentRecord);
    }
  }

  /**
   * Save consent for temporal user
   */
  private async saveConsentForTemporalUser(
    consentRecord: ConsentRecord,
  ): Promise<void> {
    try {
      console.log('🔄 Attempting to save consent for temporal user...');

      const privacyPreferences = {
        cookieConsent: consentRecord.cookieConsent,
        dataProcessingConsent: consentRecord.dataProcessingConsent,
        cookiePreferences: consentRecord.cookiePreferences,
        bannerVersion: consentRecord.bannerVersion,
        consentMethod: consentRecord.consentMethod,
        consentDurationDays: consentRecord.consentDurationDays,
        bannerText: consentRecord.bannerText,
      };

      await this.authService.acceptPrivacyPolicies(privacyPreferences);
      console.log('✅ Temporal user created with consent preferences');
    } catch (error) {
      console.error('❌ Error creating temporal user:', error);
      // Fallback to localStorage
      this.storageService.storeUserConsent(consentRecord);
    }
  }

  /**
   * Open legal document in external browser (Safari/Chrome)
   */
  openLegalDocument(type: string): void {
    const validTypes = ['privacy-policy', 'terms-service', 'cookie-policy', 'gdpr-rights'] as const;
    if (validTypes.includes(type as typeof validTypes[number])) {
      this.externalBrowser.openLegalDocument(type as typeof validTypes[number]);
    } else {
      console.warn('Unknown legal action:', type);
    }
  }

  /**
   * Toggle cookie preference
   */
  toggleCookiePreference(type: keyof CookiePreferences): void {
    if (type === 'necessary') return; // Can't disable necessary cookies

    this.cookiePreferences.update((prefs) => ({
      ...prefs,
      [type]: !prefs[type],
    }));
  }
}
